import { useScroll, useTransform } from 'framer-motion';
import Lanyard from '@/components/Lanyard';
import { Button } from '@/components/ui/button';
import { useNavigate } from "react-router-dom";
import {
  Hand,
  Linkedin,
  Github,
  Instagram,
  Mail,
  X as XIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  motion,
  useAnimation,
  useInView,
  LayoutGroup,
  AnimatePresence,
} from 'framer-motion';

import TechTicker from '@/components/TechTicker';
import {
 SiReact,
  SiPython,
  SiFirebase,
  SiTailwindcss,
  SiCss3,
  SiHtml5,
  SiCanva,
  SiVuedotjs,
  SiFigma,
  SiGit,
  SiAmazonwebservices,
  SiHeroku,
  SiGreensock,
  SiJavascript,
} from 'react-icons/si';

/* ---------------------------------
   SMALL UTIL / MINI COMPONENTS
---------------------------------- */
const HideScrollStyle = () => (
  <style>{`.hide-scrollbar{scrollbar-width:none;-ms-overflow-style:none}.hide-scrollbar::-webkit-scrollbar{display:none}`}</style>
);

const Avatar = ({ src, alt, fallback }) => (
  <div className="h-10 w-10 rounded-full bg-zinc-100 dark:bg-white/5 ring-1 ring-zinc-200 dark:ring-white/10 flex items-center justify-center overflow-hidden">
    {src ? <img src={src} alt={alt} className="h-full w-full object-cover" /> : <span className="text-xs">{fallback ?? '•'}</span>}
  </div>
);

/* ---------------------------------
   BULLET SPLITTER
---------------------------------- */
function splitBullet(s) {
  const seps = [' — ', ' – ', ' - ', ': ', '—', '–', ':', ' - '];
  for (const sp of seps) {
    const i = s.indexOf(sp);
    if (i > 0) return [s.slice(0, i).trim(), s.slice(i + sp.length).trim()];
  }
  return [s.trim(), ''];
}

/* ---------------------------------
   EDUCATION CARD
---------------------------------- */
function EducationCard({ item }) {
  const ref = useRef(null);

  const isInView = useInView(ref, {
    margin: '-40% 0px -40% 0px',
  });
  return (
    <motion.li
      ref={ref}
      layout
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.45 }}
      variants={{
        hidden: { opacity: 0, y: 22 },
        show:   { opacity: 1, y: 0, transition: { duration: 0.32, ease: 'easeOut' } },
      }}
      className="relative pl-8 mb-10"
    >
      {/* timeline dot + soft ping */}
<motion.span
  className="absolute left-0 top-5 h-3 w-3 rounded-full"
  animate={{
    scale: isInView ? 1.3 : 1,
    backgroundColor: isInView ? '#22c55e' : '#52525b',
  }}
  transition={{ duration: 0.3 }}
/>

{isInView && (
  <span className="absolute left-[2px] top-[20px] h-2.5 w-2.5 rounded-full bg-green-400 blur-[4px] animate-pulse" />
)}

      <a
        href={item.link ?? '#'}
        target={item.link ? '_blank' : undefined}
        rel={item.link ? 'noreferrer' : undefined}
        className="group block rounded-2xl border border-zinc-200/80 dark:border-white/10 bg-gradient-to-b from-white to-zinc-50 dark:from-white/[0.06] dark:to-white/[0.03] hover:from-white dark:hover:from-white/[0.09] hover:to-zinc-50 dark:hover:to-white/[0.05] transition-colors"
      >
        <div className="p-5 sm:p-6 grid grid-cols-[auto,1fr,auto] gap-4 items-start">
          <div className="mt-1">
            <Avatar src={item.logo} alt={item.school} fallback={item.school?.[0] ?? '•'} />
          </div>

          <div>
            {/* INTEGRATED LINE */}
            <h4 className="font-semibold tracking-tight text-[16px] sm:text-[18px] text-zinc-900 dark:text-white">
              {item.degree} <span className="text-zinc-400 dark:text-white/40">•</span> <span className="text-zinc-800 dark:text-white/90">{item.school}</span>
            </h4>

            {(item.meta || item.track) && (
              <p className="mt-1 text-[13px] text-zinc-600 dark:text-white/60">
                {item.meta}{item.meta && item.track ? ' — ' : ''}{item.track}
              </p>
            )}

            {item.bullets?.length > 0 && (
              <ul className="mt-3 space-y-2.5">
                {item.bullets.map((b, i) => {
                  const [lead, tail] = splitBullet(b);
                  return (
                    <li
                      key={i}
                      className="relative pl-6 text-[15px] leading-relaxed text-zinc-700 dark:text-white/80 hover:text-zinc-900 dark:hover:text-white transition-colors"
                    >
                      <span className="absolute left-0 top-[10px] h-2.5 w-2.5 rounded-full bg-gradient-to-br from-indigo-400 to-violet-400 shadow-[0_0_0_2px_rgba(0,0,0,0.06)] dark:shadow-[0_0_0_2px_rgba(255,255,255,0.08)]" />
                      <span className="font-semibold text-zinc-900 dark:text-white">{lead}</span>
                      {tail && <span className="text-zinc-700 dark:text-white/80"> — {tail}</span>}
                    </li>
                  );
                })}
              </ul>
            )}

            {item.honors?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {item.honors.map((t, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center text-[11px] font-medium px-3 py-1 rounded-full
                               bg-zinc-100 text-zinc-800 border border-zinc-200
                               dark:bg-zinc-800 dark:text-zinc-100 dark:border-zinc-700
                               transition-colors"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="text-right">
            <div className="text-sm text-zinc-700 dark:text-white/70 whitespace-nowrap">{item.dates}</div>
            {item.link && (
              <div className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 opacity-80 group-hover:opacity-100">
                View ↗
              </div>
            )}
          </div>
        </div>
      </a>
    </motion.li>
  );
}



/* ---------------------------------
   LIGHTBOX
---------------------------------- */
function Lightbox({ open, onClose, images, index, setIndex, next, prev, title }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="absolute inset-0 flex items-center justify-center p-4"
            initial={{ scale: 0.98, y: 8, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.98, y: 8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full max-w-5xl aspect-[16/9] rounded-2xl overflow-hidden bg-white dark:bg-[#0d0f14] border border-zinc-200 dark:border-white/12 ring-1 ring-black/5 dark:ring-white/5">
              <img
                src={images[index]}
                alt={`${title ?? 'Image'} ${index + 1}`}
                className="h-full w-full object-contain bg-white dark:bg-black/40"
                loading="eager"
              />

              <div className="absolute left-4 top-4 text-zinc-900 dark:text-white/90 text-sm px-2 py-1 rounded-md bg-white/70 dark:bg-black/30 border border-zinc-200 dark:border-white/10 backdrop-blur">
                {title}
              </div>

              <button
                aria-label="Close"
                onClick={onClose}
                className="absolute right-3 top-3 h-9 w-9 grid place-items-center rounded-full bg-black/10 hover:bg-black/20 dark:bg-black/40 dark:hover:bg-black/60 border border-zinc-200/60 dark:border-white/10"
              >
                <XIcon className="h-5 w-5 text-zinc-900 dark:text-white" />
              </button>

              <button
                aria-label="Previous"
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center rounded-full bg-black/10 hover:bg-black/20 dark:bg-black/40 dark:hover:bg-black/60 border border-zinc-200/60 dark:border-white/10"
              >
                <ChevronLeft className="h-5 w-5 text-zinc-900 dark:text-white" />
              </button>
              <button
                aria-label="Next"
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 grid place-items-center rounded-full bg-black/10 hover:bg-black/20 dark:bg-black/40 dark:hover:bg-black/60 border border-zinc-200/60 dark:border-white/10"
              >
                <ChevronRight className="h-5 w-5 text-zinc-900 dark:text-white" />
              </button>

              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className="h-2.5 w-2.5 rounded-full"
                    style={{
                      background: i === index ? '#111111' : 'rgba(0,0,0,.25)',
                      boxShadow: i === index ? '0 0 0 2px rgba(0,0,0,.35)' : 'none',
                      transition: 'all .2s ease',
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const projectsControls = useAnimation();

  const projectsRef = useRef(null);
  const timelineRef = useRef(null);

const { scrollYProgress } = useScroll({
  target: timelineRef,
  offset: ['start 80%', 'end 20%'],
});

const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const projectsInView = useInView(projectsRef, { once: true, margin: '0px 0px -100px 0px' });

  useEffect(() => {
    if (projectsInView) projectsControls.start('visible');
  }, [projectsInView, projectsControls]);

  const sectionVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { staggerChildren: 0.3, delayChildren: 0.5, duration: 1.2 } },
  };
  const childVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeOut' } },
  };

  const projEndRef = useRef(null);
  const [listActive, setListActive] = useState(false);
  useEffect(() => {
    if (!projEndRef.current) return;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setListActive(true), { threshold: 0.2 });
    io.observe(projEndRef.current);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const onScroll = () => {
      const top = projectsRef.current?.getBoundingClientRect().top ?? 0;
      if (top > 120) setListActive(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
useEffect(() => {
  const el = document.getElementById("cert-scroll");
  if (!el) return;

  const handleWheel = (e) => {
    const atTop = el.scrollTop === 0;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    if (
      (e.deltaY < 0 && !atTop) ||
      (e.deltaY > 0 && !atBottom)
    ) {
      e.stopPropagation();
    }
  };

  el.addEventListener("wheel", handleWheel);

  return () => el.removeEventListener("wheel", handleWheel);
}, []);

  /* ---------------------------
     DATA
  ---------------------------- */
  const cards = [
    {
      id: 'aquai',
      title: 'AquAi',
      desc: 'AI-powered aquaponics system for real-time monitoring and predictive analytics of tilapia and kangkong growth using IoT, LSTM, and computer vision.',
      img: '/images/aquAi.png',
      gallery: ['/images/thesis 1.png',
        '/images/thesis 2.png',
        '/images/pishdalogin.jpg',
        '/images/thesis.png',
        '/images/thesis 3.png',
        '/images/thesis 4.png',
        '/images/thesis 5.png',
        '/images/thesis 6 chatbot.png',
        '/images/pishdaforgetpass.jpg'],
    },
    {
      id: 'porthub',
      title: 'PortHub',
      desc: 'A customizable platform for building and personalizing websites in real time.',
      img: '/images/porthub.jpg',
      gallery: ['/images/portyland.jpg',
        '/images/portyregis.jpg',
        '/images/portyland1.jpg',
        '/images/portydash.jpg',
        '/images/portyside.jpg',
        '/images/portyside1.jpg',
        '/images/portyside2.jpg',
        '/images/portyprof.jpg'],
    },
    {
      id: 'casitas',
      title: 'Casitas Birria',
      desc: 'A vibrant restaurant website for Casitas Birria featuring menu browsing, brand storytelling, and a seamless ordering experience.',
      img: '/images/casitas.jpg',
      gallery: [
        '/images/homepage.png',
        '/images/homepage1.png',
        '/images/homepage3.png',
        '/images/homepage4.png',
        '/images/aboutus.png',
        '/images/aboutus5.png',
        '/images/menu.png',
        '/images/menu1.png',
      ],
    },
  ];

 const skills = [
  { icon: <SiReact color="#61DAFB" size={22} />, label: 'React.js', href: 'https://react.dev' },
  { icon: <SiVuedotjs color="#42B883" size={22} />, label: 'Vue.js', href: 'https://vuejs.org' },
  { icon: <SiFigma color="#F24E1E" size={22} />, label: 'Figma', href: 'https://figma.com' },
  { icon: <SiGit color="#F05032" size={22} />, label: 'Git', href: 'https://git-scm.com' },
  { icon: <SiFirebase color="#FFCA28" size={22} />, label: 'Firebase', href: 'https://firebase.google.com' },
  { icon: <SiAmazonwebservices color="#FF9900" size={22} />, label: 'AWS', href: 'https://aws.amazon.com' },
  { icon: <SiHeroku color="#430098" size={22} />, label: 'Heroku', href: 'https://heroku.com' },
  { icon: <SiGreensock color="#88CE02" size={22} />, label: 'GSAP', href: 'https://greensock.com/gsap' },
  { icon: <SiPython color="#3776AB" size={22} />, label: 'Python', href: 'https://www.python.org' },
  { icon: <SiTailwindcss color="#38BDF8" size={22} />, label: 'Tailwind CSS', href: 'https://tailwindcss.com' },
  { icon: <SiJavascript color="#F7DF1E" size={22} />, label: 'JavaScript', href: 'https://developer.mozilla.org/docs/Web/JavaScript' },
  { icon: <SiHtml5 color="#E34F26" size={22} />, label: 'HTML5', href: 'https://developer.mozilla.org/docs/Web/HTML' },
  { icon: <SiCss3 color="#1572B6" size={22} />, label: 'CSS3', href: 'https://developer.mozilla.org/docs/Web/CSS' },
  { icon: <SiCanva color="#00C4CC" size={22} />, label: 'Canva', href: 'https://www.canva.com' },
];

  const eduItemsAll = [
    {
      id: 'exp-1',
      school: 'Frontend Designer Intern',
      degree: 'Scrubbed',
      meta: 'UI/UX Design • Hybrid',
      dates: 'Nov 2025- Feb 2026',
      logo: '/images/scrubbed.jpg',
      bullets: [
        'Designed UI/UX mockups using Figma ',
        'Collaborated with developers to translate designs into responsive interfaces',
        'Improved usability by refining layouts, spacing, and user flows',
      ],
      honors: ['Internship'],
    },
    {
      id: 'ed-1',
      school: 'B.S. in Computer Engineering',
      degree: 'Holy Angel University',
      meta: 'College of Engineering • Angeles City, PH',
      dates: '2022 — 2026',
      logo: '/images/HAU LOGO.png',
      bullets: [
        'Semester — 2nd Semester, SY 2024',
        'Semester — 1st Semester, SY 2025',
      ],
      honors: ['Dean\u2019s List', 'President\u2019s List'],
    },
    {
      id: 'ed-2',
      school: 'Senior High School',
      degree: 'Saint Nathanael Academy INC.',
      track: 'Science • Technology • Engineering • Mathematics',
      dates: '2020 — 2022',
      logo: '/images/SNA.jpg',
      bullets: [
        'SY 2020\u20132021 — With Honors',
        'SY 2021\u20132022 — With Honors',
      ],
      honors: ['With Honors'],
    },
  ];

const certifications = [
  {
    title: 'Build Web Apps with Vue JS 3 & Firebase',
    org: 'Udemy',
    year: '2026',
    link: 'https://www.udemy.com/certificate/UC-d41ede47-f49d-4afb-bf56-205e249c7c18/',
  },
  {
    title: 'AI Fundamentals with IBM SkillsBuild',
    org: 'Cisco IBM Skills Build',
    year: '2026',
    link: 'https://www.netacad.com/certificates/?issuanceId=5b7c53ee-454f-4476-bc04-994b0cac9994',
  },
  {
    title: 'Introduction to Data Science',
    org: 'Cisco Networking Academy',
    year: '2026',
    link: 'https://www.netacad.com/certificates/?issuanceId=d1507b25-dcf4-4053-8065-37b6cf71431d',
  },
  {
    title: 'Introduction to IoT and Digital Transformation',
    org: 'Cisco Networking Academy',
    year: '2023',
    link: 'https://www.netacad.com/certificates/?issuanceId=0621acfa-8f31-41f0-a5d3-37d0dc5dcca7',
  },
  {
    title: 'Introduction to Modern AI',
    org: 'Cisco Networking Academy',
    year: '2026',
    link: 'https://www.netacad.com/certificates/?issuanceId=36b8f84c-49c4-40c4-aa21-d07c3dbd3424',
  },
  {
    title: 'AI Hackathon Webinar Series: AI Tools, Platforms, and Techniques',
    org: 'AI Fest Hackathon 2025',
    year: '2025',
    link: '#',
  },
  {
    title: 'AI Hackathon Webinar Series: Basics of Natural Language Processing (NLP)',
    org: 'AI Fest Hackathon 2025',
    year: '2025',
    link: '#',
  },
  {
    title: 'AI Hackathon Webinar Series: Ethics and Responsible AI',
    org: 'AI Fest Hackathon 2025',
    year: '2025',
    link: '#',
  },
  {
    title: 'From Sketch to Screen: Creating a Seamless Future with UI/UX',
    org: 'AI Fest Hackathon 2025',
    year: '2024',
    link: '#',
  },
];

const [showAllCerts, setShowAllCerts] = useState(false);
const certBtnRef = useRef(null);

const certVisible = showAllCerts
  ? certifications
  : certifications.slice(0, 3);
const [certOpen, setCertOpen] = useState(false);
const [certSlideIdx, setCertSlideIdx] = useState(0);
const [activeCertIdx, setActiveCertIdx] = useState(0);
const openCert = (idx) => {
  setLightboxOpen(false);
  setActiveCertIdx(idx);
  setCertSlideIdx(0);
  setCertOpen(true);
};

const closeCert = () => setCertOpen(false);

  const [showAllEdu, setShowAllEdu] = useState(false);
  const eduShowBtnRef = useRef(null);
  const eduVisible = showAllEdu ? eduItemsAll : eduItemsAll.slice(0, 2);

  const scrollToCenter = (el, timeout = 900) =>
    new Promise((resolve) => {
      if (!el) return resolve();
      const rect = el.getBoundingClientRect();
      const targetTop = window.scrollY + rect.top - (window.innerHeight / 2 - el.offsetHeight / 2);
      window.scrollTo({ top: targetTop, behavior: 'smooth' });

      const start = performance.now();
      const check = () => {
        const r = el.getBoundingClientRect();
        const dist = Math.abs(r.top + r.height / 2 - window.innerHeight / 2);
        const elapsed = performance.now() - start;
        if (dist < 8 || elapsed > timeout) return resolve();
        requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    });

  const toggleEdu = async () => {
    const willExpand = !showAllEdu;
    await scrollToCenter(eduShowBtnRef.current);
    setShowAllEdu(willExpand);
  };

const toggleCerts = async () => {
  const willExpand = !showAllCerts;
  await scrollToCenter(certBtnRef.current);
  setShowAllCerts(willExpand);
};

  // LIGHTBOX
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeCardIdx, setActiveCardIdx] = useState(0);
  const [activeSlideIdx, setActiveSlideIdx] = useState(0);

const openLightbox = useCallback((cardIdx, startIdx = 0) => {
  setCertOpen(false);
  setActiveCardIdx(cardIdx);
  setActiveSlideIdx(startIdx);
  setLightboxOpen(true);
}, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const nextSlide = useCallback(() => {
    const g = cards[activeCardIdx].gallery ?? [cards[activeCardIdx].img];
    setActiveSlideIdx((i) => (i + 1) % g.length);
  }, [activeCardIdx, cards]);
  const prevSlide = useCallback(() => {
    const g = cards[activeCardIdx].gallery ?? [cards[activeCardIdx].img];
    setActiveSlideIdx((i) => (i - 1 + g.length) % g.length);
  }, [activeCardIdx, cards]);

  // Stacked preview cards
  const CardPreview = ({ c, stackedIndex, stacked }) => {
    const stackedClass =
      stackedIndex === 0 ? 'md:scale-90 md:z-10 md:absolute md:left-0'
        : stackedIndex === 1 ? 'md:scale-100 md:z-20 md:absolute'
          : 'md:scale-90 md:z-10 md:absolute md:right-0';

    return (
      <motion.div
        layoutId={c.id}
        transition={{ type: 'spring', stiffness: 240, damping: 28 }}
        className={[
          stacked ? stackedClass : '',
          'relative h-[120px] w-full md:h-[100px] md:w-[80px] lg:h-[380px] lg:w-[280px]',
          'rounded-2xl overflow-hidden transform-gpu will-change-transform',
          'transition-transform duration-300 ease-out hover:scale-105 md:hover:scale-110',
          'hover:z-30 cursor-pointer shadow-md hover:shadow-lg bg-zinc-100 dark:bg-black/5',
        ].join(' ')}
        onClick={() => openLightbox(stackedIndex, 0)}
      >
        <img src={c.img} alt={c.title} className="h-full w-full object-cover" decoding="async" loading="eager" />
        <div className="absolute inset-0 flex flex-col justify-end p-3 bg-gradient-to-t from-black/20 via-black/0 to-transparent dark:from-black/80">
          <span className="font-jetbrains font-extrabold text-zinc-900 dark:text-white">{c.title}</span>
          <span className="font-jetbrains text-[10px] text-zinc-700 dark:text-stone-300 line-clamp-2 md:line-clamp-none">{c.desc}</span>
        </div>
      </motion.div>
    );
  };

  return (
    <LayoutGroup>
      <HideScrollStyle />

      {/* HERO */}
      <div id="home" className="relative min-h-[80vh] w-full overflow-hidden mb-10 flex flex-col bg-white dark:bg-transparent">
        <div className="relative z-10 flex min-h-[70vh] mx-auto w-full max-w-[1300px] flex-col">
          <div className="relative z-10 w-[90%] mt-12 px-6 h-[85%] text-center sm:text-start flex flex-col justify-center pointer-events-auto">
            <div className="flex items-center gap-2 mt-16 mb-6 transition-colors">
              <Hand className="h-4 text-emerald-500" />
              <span className="text-sm font-jetbrains text-zinc-800 dark:text-white/90">Hey! It&apos;s me ( Audrey Tan ).</span>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
              className="inline-flex items-center gap-2 self-center sm:self-start rounded-full px-4 py-2 bg-emerald-50 dark:bg-white/5 border border-emerald-200 dark:border-white/10 backdrop-blur-sm text-[12px] font-jetbrains text-emerald-700 dark:text-white/80"
            >
              <span className="inline-block h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              Open to Opportunities
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.2 }}
              className="mt-6 leading-[1.05] font-[Geist] font-semibold text-center sm:text-left text-zinc-900 dark:text-white"
              style={{ fontSize: 'clamp(32px, 6vw, 64px)' }}
            >
              <span className="block">Developer &amp; UI/UX Designer</span>
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-[#2563eb] via-[#7c3aed] to-[#f472b6] dark:from-[#8ec5ff] dark:via-[#a78bfa] dark:to-[#f472b6]">
                Aspiring Web&nbsp;Developer
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.3 }}
              className="mt-5 text-zinc-700 dark:text-white/80 font-jetbrains text-[14px] sm:text-[18px] lg:text-[16px] leading-relaxed max-w-2xl mx-auto sm:mx-0"
            >
              I am a Computer Engineering graduate specializing in UI/UX design, focused on creating simple, clear, and user-friendly solutions. While design is my strength, I am also eager to grow in software development and take on challenges that combine design and technology.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
              className="mt-8 flex flex-wrap items-center gap-4 text-[12px] font-jetbrains"
            >
              <a href="https://www.linkedin.com/in/audrey-tan-72a514351/" className="group inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2 hover:bg-zinc-50 dark:hover:bg-white/10 transition-colors" aria-label="LinkedIn">
                <Linkedin className="h-4 w-4 text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white" />
                <span className="text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white">LinkedIn</span>
              </a>
              <a href="https://github.com/AudreyTan03" className="group inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2 hover:bg-zinc-50 dark:hover:bg-white/10 transition-colors" aria-label="GitHub">
                <Github className="h-4 w-4 text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white" />
                <span className="text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white">GitHub</span>
              </a>
              <a href="https://www.instagram.com/shob.hi111/" className="group inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2 hover:bg-zinc-50 dark:hover:bg-white/10 transition-colors" aria-label="Instagram">
                <Instagram className="h-4 w-4 text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white" />
                <span className="text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white">Instagram</span>
              </a>
              <a href="mailto:audreyannetan13@gmail.com" className="group inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2 hover:bg-zinc-50 dark:hover:bg-white/10 transition-colors" aria-label="Email">
                <Mail className="h-4 w-4 text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white" />
                <span className="text-zinc-700 dark:text-white/80 group-hover:text-zinc-900 dark:group-hover:text-white">Email</span>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.5 }}
              className="mt-8"
            >
              <motion.a
                href="/Tan, Audrey- Resume.pdf"
                target="_blank"
                rel="noopener noreferrer"
                initial="rest"
                whileHover="hover"
                whileTap="tap"
                variants={{
                  rest: { y: 0 },
                  hover: { y: -2 },
                  tap: { scale: 0.96 },
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="group inline-flex items-center gap-2 rounded-full px-5 py-2.5
                           border border-zinc-200 dark:border-white/10
                           bg-white/80 dark:bg-white/5 backdrop-blur-md
                           text-zinc-800 dark:text-white
                           shadow-[0_4px_14px_rgba(0,0,0,0.06)]
                           hover:shadow-[0_8px_28px_rgba(0,0,0,0.12)]
                           hover:bg-zinc-50 dark:hover:bg-white/10
                           transition-all duration-300"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 group-hover:animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span className="text-sm font-medium tracking-tight">
                  My Resume
                </span>
                <motion.span
                  variants={{
                    rest: { x: 0, opacity: 0.6 },
                    hover: { x: 4, opacity: 1 },
                  }}
                  transition={{ duration: 0.2 }}
                  className="text-zinc-500 dark:text-white/60"
                >
                  →
                </motion.span>
              </motion.a>
            </motion.div>
          </div>

          {/* Lanyard positioned behind content */}
          <div className="pointer-events-none select-none">
            <div className="hidden lg:block absolute right-[-80px] bottom-0 w-[36%] max-w-[520px] aspect-[3/5] z-[1] opacity-90">
              <Lanyard />
            </div>
          </div>
        </div>

        {/* FULL-WIDTH, NEVER-STOPPING TICKER */}
        <div className="mt-6 w-full relative z-10">
          <TechTicker
            items={skills}
            speed={50}
            gap={28}
            height={88}
            ariaLabel="Technologies I use"
            pauseOnHover={false}
            edgeFade={true}
          />
        </div>
      </div>

      {/* EDUCATION */}
      <section id="education" className="mx-auto w-full max-w-[1300px] px-6 py-20 scroll-mt-24">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={{
            hidden: { opacity: 0, y: 24 },
            show:   { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut', when: 'beforeChildren', staggerChildren: 0.08 } },
          }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10"
        >
          {/* Left header */}
          <div className="lg:col-span-5">
            <motion.div
              variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
              className="text-[12px] tracking-[0.18em] uppercase text-emerald-700 dark:text-lime-300/90"
            >
              Education & Experience
            </motion.div>

            <motion.h2
              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } }}
              className="mt-4 font-[Geist] font-semibold leading-[1.06] tracking-tight text-zinc-900 dark:text-white"
              style={{ fontSize: 'clamp(38px,5vw,68px)' }}
            >
              My Academic &amp; Professional Journey
            </motion.h2>

            <motion.p
              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, delay: 0.05 } } }}
              className="mt-6 max-w-prose text-zinc-700 dark:text-zinc-400 text-lg"
            >
              Learning, Growing, Achieving.
            </motion.p>
          </div>

          {/* Right timeline column */}
          <div className="lg:col-span-7">
            <div ref={timelineRef} className="relative pl-2">
              {/* Growing vertical line */}
              <motion.span
                style={{ height: lineHeight }}
                className="absolute left-[6px] top-0 w-[2px] origin-top 
                           bg-gradient-to-b 
                           from-emerald-400 
                           via-emerald-500/80 
                           to-emerald-500/0
                           opacity-80"
              />

              {/* Staggered cards */}
              <motion.ol
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } } }}
                className="relative"
              >
                <AnimatePresence initial={false}>
                  {eduVisible.map((item) => (
                    <EducationCard key={item.id} item={item} />
                  ))}
                </AnimatePresence>
              </motion.ol>
            </div>

            <div className="flex justify-center">
              <motion.button
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
                ref={eduShowBtnRef}
                onClick={toggleEdu}
                aria-expanded={showAllEdu}
                className="mt-6 text-zinc-700 dark:text-zinc-200 hover:text-zinc-900 dark:hover:text-white transition-colors relative group"
              >
                <span className="absolute left-0 right-0 -bottom-1 h-px bg-gradient-to-r from-transparent via-zinc-600 dark:via-white/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform origin-center" />
                {showAllEdu ? 'Show less' : 'Show more'}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* PROJECTS */}
      <div id="projects" className="transition-all border-y border-zinc-200 dark:border-white/10 overflow-visible relative" ref={projectsRef}>
        <div className="mx-auto w-full max-w-[1300px] min-h-[120vh] flex flex-col items-center lg:flex-row lg:items-center lg:justify-center pt-20 pb-8">
          <motion.div
            initial="hidden"
            animate={projectsControls}
            variants={sectionVariants}
            className={[
              'relative w-full lg:w-[60%] flex flex-col items-start pointer-events-auto px-6 transition-all duration-300',
              listActive ? 'h-auto lg:h-full' : 'h-full',
            ].join(' ')}
          >
            <motion.span variants={childVariants} className="font-jaro text-zinc-900 dark:text-white text-[50px] lg:text-[100px] text-center lg:text-start transition-all">
              COME EXPLORE MY PROJECTS
            </motion.span>
          </motion.div>

          <div
            className={[
              'px-10 w-full md:w-[40%] flex items-center justify-center md:relative transition-all duration-300',
              listActive ? 'h-0 overflow-hidden md:h-0' : 'h-[500px] md:h-[480px] lg:h-[520px]',
            ].join(' ')}
          >
            <AnimatePresence initial={false} mode="wait">
              {!listActive && cards.map((c, i) => (
                <CardPreview key={c.id} c={c} stackedIndex={i} stacked />
              ))}
            </AnimatePresence>
          </div>
        </div>

        <div ref={projEndRef} className="w-full h-6" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={listActive ? { opacity: 1, y: 0 } : { opacity: 0.35, y: 20 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="mx-auto w-full max-w-[1300px] px-6 pt-6 pb-12"
        >
          <div className="flex items-end justify-between mb-8">
            <h3 className="font-jaro text-zinc-900 dark:text-white text-[32px] sm:text-[40px] lg:text-[56px]">
              Projects
            </h3>

            <motion.button
              onClick={() => navigate("/projects-all")}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.96 }}
              className="group flex items-center gap-2 text-sm font-medium
                         text-zinc-600 dark:text-white/70
                         hover:text-zinc-900 dark:hover:text-white
                         transition-all"
            >
              View all
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </motion.button>
          </div>

          <div className="hide-scrollbar flex gap-8 overflow-x-auto snap-x snap-mandatory pb-6 -mx-6 px-6">
            <AnimatePresence initial={false} mode="wait">
              {listActive &&
                cards.slice(0, 3).map((c, idx) => (
                  <motion.article
                    key={c.id}
                    layoutId={c.id}
                    transition={{ type: 'spring', stiffness: 220, damping: 26 }}
                    onClick={() => navigate(`/projects-all?project=${c.id}`)}
                    className="group snap-center shrink-0 relative h-[420px] w-[320px]
                               rounded-2xl overflow-hidden cursor-pointer
                               bg-gradient-to-b from-white to-zinc-50 dark:from-[#1b1c20] dark:to-[#111217]
                               border border-zinc-200 dark:border-white/10 ring-1 ring-black/5 dark:ring-white/5
                               shadow-[0_12px_40px_rgba(0,0,0,.08)] dark:shadow-[0_12px_40px_rgba(0,0,0,.35)]
                               hover:shadow-[0_20px_56px_rgba(0,0,0,.15)] dark:hover:shadow-[0_20px_56px_rgba(0,0,0,.55)]
                               hover:border-zinc-300 dark:hover:border-white/20 hover:scale-[1.03]
                               transition-all duration-300"
                  >
                    <div className="h-[70%] w-full relative overflow-hidden">
                      <img src={c.img} alt={c.title} className="h-full w-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-black/0 to-transparent dark:from-black/65 dark:via-black/20 dark:to-transparent" />

                      <div className="absolute top-3 left-3">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -inset-1 rounded-full blur-md
                                     bg-gradient-to-r from-amber-400/40 to-fuchsia-500/40
                                     opacity-50 transition-opacity duration-300
                                     group-hover:opacity-80"
                        />
                        <div
                          className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full
                                     text-[10px] font-semibold tracking-wide uppercase
                                     bg-white/85 dark:bg-zinc-900/85 backdrop-blur-md
                                     text-zinc-900 dark:text-white
                                     border border-zinc-200/80 dark:border-white/25
                                     ring-1 ring-black/5 dark:ring-white/10
                                     shadow-[0_2px_8px_rgba(0,0,0,.10)] dark:shadow-[0_8px_24px_rgba(0,0,0,.45)]"
                        >
                          <svg
                            viewBox="0 0 20 20"
                            className="h-[12px] w-[12px] flex-none text-amber-600 dark:text-amber-400"
                            fill="currentColor" aria-hidden="true"
                          >
                            <path d="M10 2.5l2.3 4.66 5.15.75-3.72 3.63.88 5.13L10 14.9l-4.61 2.42.88-5.13L2.54 7.9l5.15-.75L10 2.5z"/>
                          </svg>
                          Featured
                        </div>
                      </div>
                    </div>

                    <div className="h-[30%] flex flex-col justify-between p-4">
                      <div>
                        <h4 className="text-zinc-900 dark:text-white font-semibold text-[18px] tracking-tight leading-snug">
                          {c.title}
                        </h4>
                        <p className="mt-1.5 text-[13px] text-zinc-700 dark:text-white/70 leading-relaxed line-clamp-2">
                          {c.desc}
                        </p>
                      </div>

                      <div className="mt-3 flex justify-end">
                        <span className="text-[12px] text-indigo-600 dark:text-indigo-400 font-medium hover:underline">
                          View →
                        </span>
                      </div>
                    </div>

                    {/* PROJECT LIGHTBOX */}
                    <Lightbox
                      open={lightboxOpen}
                      onClose={closeLightbox}
                      images={
                        cards[activeCardIdx]?.gallery?.length
                          ? cards[activeCardIdx].gallery
                          : [cards[activeCardIdx]?.img]
                      }
                      index={activeSlideIdx}
                      setIndex={setActiveSlideIdx}
                      next={nextSlide}
                      prev={prevSlide}
                      title={cards[activeCardIdx]?.title}
                    />
                  </motion.article>
                ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* CERT LIGHTBOX */}
      <Lightbox
        open={certOpen}
        onClose={closeCert}
        images={certifications[activeCertIdx]?.images || []}
        index={certSlideIdx}
        setIndex={setCertSlideIdx}
        next={() =>
          setCertSlideIdx((i) =>
            (i + 1) % (certifications[activeCertIdx]?.images?.length || 1)
          )
        }
        prev={() =>
          setCertSlideIdx((i) =>
            (i - 1 + (certifications[activeCertIdx]?.images?.length || 1)) %
            (certifications[activeCertIdx]?.images?.length || 1)
          )
        }
        title={certifications[activeCertIdx]?.title}
      />

      {/* CERTIFICATIONS */}
      <section id="certifications" className="mx-auto w-full max-w-[1300px] px-6 py-20 scroll-mt-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

            {/* LEFT SIDE */}
            <div className="lg:col-span-5">
              <p className="text-[12px] tracking-[0.18em] uppercase text-emerald-700 dark:text-lime-300/90">
                Certifications
              </p>
              <h2
                className="mt-3 font-[Geist] font-semibold tracking-tight text-zinc-900 dark:text-white"
                style={{ fontSize: 'clamp(32px,4vw,52px)' }}
              >
                Verified Skills &amp; Achievements
              </h2>
            </div>

            {/* RIGHT SIDE */}
            <div className="lg:col-span-7">
              <div
                id="cert-scroll"
                className="flex flex-col gap-6 max-h-[500px] overflow-y-auto pr-2 hide-scrollbar scroll-smooth"
                style={{ scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}
              >
                <AnimatePresence initial={false}>
                  {certVisible.map((cert, i) => (
                    <motion.a
                      href={cert.link}
                      key={i}
                      target="_blank"
                      rel="noreferrer"
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      whileHover={{ x: 6 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 18,
                        mass: 0.6,
                      }}
                      className="group relative flex items-center justify-between py-4 px-2
                                 text-zinc-600 dark:text-white/70
                                 hover:text-zinc-900 dark:hover:text-white
                                 transition-all duration-300"
                    >
                      <span
                        className="absolute left-0 bottom-0 h-[1px] w-0
                                   bg-zinc-300
                                   group-hover:w-full
                                   transition-all duration-300 ease-out"
                      />
                      <div>
                        <p className="text-[16px] font-medium tracking-tight
                                      text-zinc-700 dark:text-white/80
                                      group-hover:text-zinc-900 dark:group-hover:text-white
                                      transition duration-300">
                          {cert.title}
                        </p>
                        <p className="text-[13px] text-zinc-500 dark:text-white/50">
                          {cert.org}
                        </p>
                      </div>

                      <div className="flex items-center gap-4">
                        <span className="text-[12px] text-zinc-400">
                          {cert.year}
                        </span>
                        <motion.span
                          className="text-zinc-400 dark:text-white/40"
                          initial={{ x: 0 }}
                          whileHover={{ x: 6 }}
                          transition={{ type: 'spring', stiffness: 250, damping: 20 }}
                        >
                          →
                        </motion.span>
                      </div>
                    </motion.a>
                  ))}
                </AnimatePresence>
              </div>

              <div className="flex justify-center mt-4">
                <motion.button
                  ref={certBtnRef}
                  onClick={toggleCerts}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="text-zinc-700 dark:text-zinc-200 hover:text-zinc-900 dark:hover:text-white transition-colors relative group"
                >
                  <span className="absolute left-0 right-0 -bottom-1 h-px bg-gradient-to-r from-transparent via-zinc-600 dark:via-white/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform origin-center" />
                  {showAllCerts ? 'Show less' : 'Show more'}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA / CONTACT */}
      <section id="contact" className="mx-auto w-full max-w-[1300px] px-6 pt-32 pb-6 scroll-mt-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 12 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="rounded-[28px] bg-white dark:bg-[#0f1014] border border-zinc-200 dark:border-white/5 shadow-[0_12px_40px_rgba(0,0,0,.08)] dark:shadow-[0_12px_40px_rgba(0,0,0,.25)] px-6 sm:px-10 lg:px-16 py-20 lg:py-28 text-center mt-6"
        >
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-emerald-50 dark:bg-white/5 border border-emerald-200 dark:border-white/10 text-[12px] tracking-wide text-emerald-700 dark:text-white/80">
            <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
            Available for work
          </div>

          <h2 className="mt-6 font-[Geist] font-semibold leading-[1.05] text-zinc-900 dark:text-white" style={{ fontSize: 'clamp(32px, 6vw, 64px)' }}>
            Let&apos;s create your
            <br className="hidden sm:block" /> next big idea.
          </h2>

          {/* ✅ FIXED: Single motion.a — no nested wrapper */}
          <div className="mt-8 flex justify-center">
            <motion.a
              href="https://mail.google.com/mail/?view=cm&to=audreyannetan13@gmail.com"
              target="_blank"
              rel="noreferrer"
              initial="rest"
              whileHover="hover"
              whileTap="tap"
              variants={{
                rest: { scale: 1, rotate: 0 },
                hover: { scale: 1.07, rotate: [0, -0.4, 0.4, 0] },
                tap: { scale: 0.95 },
              }}
              transition={{
                rotate: { duration: 0.25, ease: 'easeInOut' },
                scale: { type: 'spring', stiffness: 250, damping: 18 },
              }}
              className="relative group inline-flex items-center justify-center
                         rounded-full px-6 py-5 overflow-hidden
                         border border-zinc-200 dark:border-white/15
                         shadow-[0_6px_20px_rgba(0,0,0,0.08)]
                         hover:shadow-[0_10px_30px_rgba(0,0,0,0.15)]
                         text-zinc-800 dark:text-white
                         cursor-pointer"
            >
              {/* FILL LAYER */}
              <motion.span
                variants={{
                  rest: { y: '100%' },
                  hover: { y: '0%' },
                }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 rounded-full
                           bg-white/90 dark:bg-white/10
                           group-hover:bg-white transition-colors duration-300"
              />

              {/* BORDER GLOW */}
              <span className="absolute inset-0 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 transition duration-300" />

              {/* TEXT SLIDE */}
              <div className="relative z-10 overflow-hidden h-[20px] flex items-center">
                <motion.span
                  variants={{
                    rest: { y: 0, color: '#71717a' },
                    hover: { y: -24, color: '#111111' },
                  }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="block font-medium"
                >
                  Contact Me
                </motion.span>
                <motion.span
                  variants={{
                    rest: { y: 22, color: '#71717a' },
                    hover: { y: 0, color: '#111111' },
                  }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-0 font-medium"
                >
                  Contact Me
                </motion.span>
              </div>
            </motion.a>
          </div>
        </motion.div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-zinc-600 dark:text-white/60 text-sm px-2 py-3">
          <p className="font-geist opacity-80">
            &copy; 2026 Audrey Tan. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            {[
              {
                href: 'https://www.linkedin.com/in/audrey-tan-72a514351/',
                icon: <Linkedin className="h-5 w-5" />,
                label: 'LinkedIn',
              },
              {
                href: 'https://github.com/AudreyTan03',
                icon: <Github className="h-5 w-5" />,
                label: 'GitHub',
              },
              {
                href: 'https://www.instagram.com/shob.hi111/',
                icon: <Instagram className="h-5 w-5" />,
                label: 'Instagram',
              },
              {
                href: 'mailto:audreyannetan13@gmail.com',
                icon: <Mail className="h-5 w-5" />,
                label: 'Email',
              },
            ].map((item, i) => (
              <a
                key={i}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="group relative flex items-center justify-center
                           text-zinc-600 dark:text-white/60
                           hover:text-zinc-900 dark:hover:text-white
                           transition-colors"
              >
                {item.icon}
                <span
                  className="pointer-events-none absolute -top-11 left-1/2 -translate-x-1/2
                             whitespace-nowrap px-3 py-1.5 text-[11px] font-medium
                             rounded-full
                             bg-white/90 dark:bg-zinc-900/90
                             backdrop-blur-md
                             text-zinc-800 dark:text-white
                             border border-zinc-200/70 dark:border-white/10
                             shadow-[0_6px_20px_rgba(0,0,0,0.08)]
                             opacity-0 scale-95 translate-y-1
                             group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0
                             transition-all duration-200 ease-out"
                >
                  {item.label}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>
    </LayoutGroup>
  );
}